import type { PulsarAction, PulsarConfig, PulsarJob, PulsarServer } from '../types/pulsar';

export const pulsarActionLabel = (action: PulsarAction) => ({
  copy_config: 'Copy to templates', snapshot: 'Snapshot', reload: 'Reload settings', configure: 'Save settings',
})[action] ?? action;

export const pulsarJobActive = (job: PulsarJob) => ['queued', 'pending', 'leased', 'running'].includes(job.state);
export const pulsarJobLabel = (state: string) => ({ queued: 'Queued', pending: 'Pending', leased: 'Starting', running: 'Running', succeeded: 'Completed', failed: 'Failed', cancelled: 'Cancelled' }[state] ?? state);

export function pulsarPathError(path: string): string | null {
  if (!path.trim()) return 'Enter a path relative to the Minecraft server directory.';
  if (path.length > 1024 || /[\x00-\x1f\x7f]/.test(path)) return 'Enter a path of up to 1,024 characters without control characters.';
  if (/^[\\/]|^[a-z]:/i.test(path) || path.includes('\\')) return 'Use a server-relative path with forward slashes, such as config/example.toml.';
  if (path.split('/').some(part => part === '..') || path.split('/').every(part => part === '.' || part === '')) return 'Choose a path inside the server directory without .. segments.';
  return null;
}

export function pulsarConfigError(config: PulsarConfig): string | null {
  if (!Number.isFinite(config.minutes_between_snapshots) || config.minutes_between_snapshots <= 0) return 'Snapshot interval must be a number of minutes greater than zero.';
  if (!Number.isFinite(config.minutes_before_deletion) || config.minutes_before_deletion <= 0) return 'Snapshot retention must be a number of minutes greater than zero.';
  const paths = [...config.folders_to_snapshot, config.snapshots_path];
  const invalid = paths.find(path => pulsarPathError(path));
  if (invalid !== undefined) return `${invalid || 'Snapshot path'}: ${pulsarPathError(invalid)}`;
  if (new Set(config.folders_to_snapshot).size !== config.folders_to_snapshot.length) return 'List each snapshot folder once.';
  return null;
}

/** A failed/stalled browser poll must never leave a green actionable status. */
export function pulsarReady(server: PulsarServer | null, lastSync: number, now: number, error: string | null): boolean {
  return !!server?.available && !!server.report?.available && !!server.report.config && !!server.report.enabled && !server.report.pulsar_present
    && !!server.report.hook_installed && !error && lastSync > 0 && now - lastSync < 20_000;
}

export const pulsarWhen = (seconds: number | null | undefined) => seconds ? new Date(seconds * 1000).toLocaleString() : 'Not reported';
export function pulsarBytes(bytes: number | undefined): string {
  if (bytes === undefined || !Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const unit = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), 4);
  return `${(bytes / 1024 ** unit).toFixed(1)} ${['B', 'KiB', 'MiB', 'GiB', 'TiB'][unit]}`;
}
